import { ReferralFormData } from '../context';
import {
  PHAdviceOptions,
  PHPersonalIntentOptions,
  PHSelfEmpSmallBusIntentOptions,
  PHTaxIssueOptions,
  TaxAdviceOptions,
  TaxIntentOptions,
  TaxServiceOptions
} from '../enums';
import {
  AdviceStep,
  IntentStep,
  PHAdviceStep,
  PHPersonalIntentStep,
  PHSelfEmpSmallBusIntentStep,
  PHSomethingElseStep,
  PHTaxIssuesStep,
  PHUrgencyStep,
  QuestionsStep,
  RamseyConnectAdviceStep
} from '../components';

// THE ORDER IN WHICH THESE COMPONENTS GET PUSHED INTO THE ARRAY DIRECTLY
// INFLUENCES THE ORDER THEY RENDER IN THE REFERRAL FORM FLOW

function generatePHQuestionsFlow(referral: ReferralFormData) {
  const paulHoodQuestionComponents = [];
  if (!isSmallBusinessSelected(referral) && !isSelfEmployedSelected(referral)) {
    paulHoodQuestionComponents.push(<PHPersonalIntentStep />);
  }
  if (isSmallBusinessSelected(referral) || isSelfEmployedSelected(referral)) {
    paulHoodQuestionComponents.push(<PHSelfEmpSmallBusIntentStep />);
  }
  if (isTaxIssuesSelected(referral)) {
    paulHoodQuestionComponents.push(<PHTaxIssuesStep />);
  }
  if (
    isTaxIssueSomethingElse(referral) ||
    isSmallBusSelfEmpSomethingElse(referral) ||
    isPHIntentSomethingElse(referral)
  ) {
    paulHoodQuestionComponents.push(<PHSomethingElseStep />);
  }
  paulHoodQuestionComponents.push(<PHUrgencyStep />);
  if (isPHIntentGeneralQuestions(referral)) {
    paulHoodQuestionComponents.push(<PHAdviceStep />);
  }
  return paulHoodQuestionComponents;
}

function generateBaselineFlow(referral: ReferralFormData) {
  const baselineComponents = [];

  if (!isSmallBusinessSelected(referral)) {
    baselineComponents.push(<IntentStep />);
  }
  if (isOnlyAdviceSelected(referral) && !isSmallBusinessSelected(referral)) {
    baselineComponents.push(<AdviceStep />);
  }
  if (
    isOnlyAdviceSelected(referral) &&
    !isSmallBusinessSelected(referral) &&
    !isOnlyOtherSelected(referral)
  ) {
    baselineComponents.push(<RamseyConnectAdviceStep />);
  }

  return baselineComponents;
}

function generateQuestionsStepForAllFlows(referral: ReferralFormData) {
  if (
    (!isSmallBusinessSelected(referral) && isOnlyAdviceSelected(referral)) ||
    isPHAdviceSomethingElse(referral)
  ) {
    return <QuestionsStep />;
  }
}

function isSmallBusinessSelected(referral: ReferralFormData) {
  return referral.interests.includes(TaxServiceOptions.small_business.value);
}

function isSelfEmployedSelected(referral: ReferralFormData) {
  return referral.interests.includes(TaxServiceOptions.self_employed.value);
}

function isOnlyAdviceSelected(referral: ReferralFormData) {
  const taxIntents = referral.tax_intents.filter((s) => s !== '');
  const includesAdvice = taxIntents.includes(
    TaxIntentOptions.give_me_advice.value
  );
  const onlyOne = taxIntents.length === 1;
  return includesAdvice && onlyOne;
}

function isOnlyOtherSelected(referral: ReferralFormData) {
  const taxAdvice = referral.tax_advice.filter((s) => s !== '');
  const includesOther = taxAdvice.includes(TaxAdviceOptions.other.value);
  const onlyOne = taxAdvice.length === 1;
  return includesOther && onlyOne;
}

function isTaxIssuesSelected(referral: ReferralFormData) {
  const pHPersonalIntents = referral.ph_personal_intents?.filter(
    (s) => s !== ''
  );
  const pHSelfEmpSmallBusIntents =
    referral.ph_self_employed_small_business_intent?.filter((s) => s !== '');

  if (
    pHPersonalIntents?.includes(
      PHPersonalIntentOptions.help_with_tax_issues.value
    )
  ) {
    return true;
  } else {
    return pHSelfEmpSmallBusIntents?.includes(
      PHSelfEmpSmallBusIntentOptions.help_with_tax_issues.value
    );
  }
}

function isTaxIssueSomethingElse(referral: ReferralFormData) {
  const taxIssue = referral.ph_tax_issues?.filter((s) => s !== '');
  return taxIssue?.includes(PHTaxIssueOptions.something_else.value);
}

function isPHIntentSomethingElse(referral: ReferralFormData) {
  const pHPersonalIntents = referral.ph_personal_intents?.filter(
    (s) => s !== ''
  );
  return pHPersonalIntents?.includes(
    PHPersonalIntentOptions.something_else.value
  );
}

function isSmallBusSelfEmpSomethingElse(referral: ReferralFormData) {
  const pHSmallBusSelfEmpIntents =
    referral.ph_self_employed_small_business_intent?.filter((s) => s !== '');
  return pHSmallBusSelfEmpIntents?.includes(
    PHSelfEmpSmallBusIntentOptions.something_else.value
  );
}

function isPHIntentGeneralQuestions(referral: ReferralFormData) {
  const pHPersonalIntents = referral.ph_personal_intents?.filter(
    (s) => s !== ''
  );
  const pHSelfEmpSmallBusIntents =
    referral.ph_self_employed_small_business_intent?.filter((s) => s !== '');

  if (
    pHPersonalIntents?.includes(
      PHPersonalIntentOptions.answer_general_questions.value
    )
  ) {
    return true;
  } else {
    return pHSelfEmpSmallBusIntents?.includes(
      PHSelfEmpSmallBusIntentOptions.answer_general_questions.value
    );
  }
}

function isPHAdviceSomethingElse(referral: ReferralFormData) {
  const pHAdvice = referral.ph_tax_advice?.filter((s) => s !== '');
  return pHAdvice?.includes(PHAdviceOptions.something_else.value);
}

export {
  generateBaselineFlow,
  generatePHQuestionsFlow,
  generateQuestionsStepForAllFlows
};
